import { getOpenAILeadContext } from './leadTracking.js';
import { executeRecaptcha } from './recaptcha.js';
import { getTurnstileToken } from './turnstile.js';

export const REQUEST_RECEIPT_KEY = 'tda_consult_request_receipt_v1';

// Contact details go only to the existing practice intake endpoint. They are
// never placed in a URL, a dataLayer event or browser storage.
export function buildConsultationRequest(form, campaign = {}) {
  const name = String(form.name || '').trim();
  const email = String(form.email || '').trim();
  const phone = String(form.phone || '').trim();
  if (name.length < 2 || name.length > 120) throw new Error('Please enter your name.');
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Please enter a valid email address.');
  if (phone.replace(/\D/g, '').length < 10 || phone.length > 30) throw new Error('Please enter a valid phone number.');
  if (!form.agreed) throw new Error('Please acknowledge the consultation fee and contact permission.');
  const date = /^\d{4}-\d{2}-\d{2}$/.test(form.preferredDate || '') ? form.preferredDate : '';
  if (date && [0, 5, 6].includes(new Date(date + 'T12:00:00').getDay())) throw new Error('Please choose Monday through Thursday, or leave the date open.');
  if (date && new Date(date + 'T23:59:59').getTime() < Date.now()) throw new Error('Please choose a future date, or leave the date open.');
  const time = ['morning', 'afternoon', 'anytime'].includes(form.preferredTime) ? form.preferredTime : 'anytime';
  const method = form.contactMethod === 'email' ? 'email' : 'phone';
  const attribution = {};
  for (const key of ['utm_source','utm_medium','utm_campaign','utm_content','gclid','gbraid','wbraid']) {
    if (/^[a-z0-9_.-]{1,200}$/i.test(campaign[key] || '')) attribution[key] = campaign[key];
  }
  return {
    request_key: form.requestKey, preferred_date: date || undefined,
    measurement_consent: campaign.measurement_consent === 'granted' ? 'granted' : 'denied',
    consent_policy: 'tda_cookie_consent_v2',
    name, email, phone, consultType: 'signature', service_interest: 'other',
    preferred_contact_method: method, preferred_contact_time: time,
    source: 'signature-consult-request', source_page: '/consult',
    message: `Signature Consultation request. Preferred date: ${date || 'Flexible'}; time: ${time}. Patient acknowledged the $300 fee credited toward treatment begun within 30 days and consented to scheduling contact. Requested time requires team confirmation.`,
    company_website: String(form.website || '').slice(0,200), ...attribution,
    ...getOpenAILeadContext(),
  };
}

export async function submitConsultationRequest(invoke, form, campaign) {
  const payload = buildConsultationRequest(form, campaign);
  // Invisible reCAPTCHA and Turnstile tokens — both verified server-side.
  // Each resolves empty when its script is unavailable, so a blocked script
  // never blocks a real patient (the endpoint holds tokenless submissions to a
  // much tighter cap).
  const [recaptchaToken, turnstileToken] = await Promise.all([
    executeRecaptcha(),
    getTurnstileToken('capture-lead'),
  ]);
  payload.recaptchaToken = recaptchaToken || undefined;
  payload.turnstile_token = turnstileToken || undefined;
  const response = await invoke('capture-lead', payload);
  const data = response?.data;
  if (data?.success !== true || typeof data.lead_id !== 'string' || !data.lead_id) {
    throw new Error('We could not confirm that your request was saved. Please call (727) 228-6846.');
  }
  return true;
}